/**
 * AnimatedAccordion — Reusable expand/collapse card section
 *
 * Smooth height animation via scrollHeight measurement.
 * Children receive `showRows` boolean for stagger animation.
 * Used in Activity card, reusable anywhere.
 */

import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { T } from '@/styles/tokens';

const DURATION = 350;
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

interface AnimatedAccordionProps {
  icon: ReactNode;
  label: string;
  total: string;
  totalColor?: string;
  defaultOpen?: boolean;
  /** Optional badge rendered next to the label */
  badge?: ReactNode;
  /** Override inner drawer background (default: T.accordionBg) */
  innerBg?: string;
  /** Override inner drawer border-radius (default: '0 0 12px 12px') */
  innerRadius?: string;
  children: (showRows: boolean) => ReactNode;
}

export function AnimatedAccordion({
  icon,
  label,
  total,
  totalColor = T.text,
  defaultOpen = false,
  badge,
  innerBg,
  innerRadius,
  children,
}: AnimatedAccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showRows, setShowRows] = useState(defaultOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const [pressed, setPressed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  // Initialize height for defaultOpen
  const [height, setHeight] = useState<number | 'auto'>(defaultOpen ? 'auto' : 0);

  const toggle = useCallback(() => {
    if (isAnimating) return;
    const el = innerRef.current;
    if (!el) return;

    setIsAnimating(true);

    if (isOpen) {
      // Collapse: hide rows instantly, then animate height down
      setShowRows(false);
      const measured = el.scrollHeight;
      setHeight(measured);
      // Force reflow so browser sees the explicit height before we set 0
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetHeight;
      requestAnimationFrame(() => {
        setHeight(0);
        setIsOpen(false);
      });
    } else {
      // Expand: animate height up, then stagger rows
      setIsOpen(true);
      const measured = el.scrollHeight;
      setHeight(measured);
      // Trigger row stagger after initial delay
      setTimeout(() => setShowRows(true), 80);
    }
  }, [isOpen, isAnimating]);

  const handleTransitionEnd = useCallback(() => {
    setIsAnimating(false);
    if (isOpen) {
      // After expand, set auto for content reflow
      setHeight('auto');
    }
  }, [isOpen]);

  // Sync defaultOpen changes
  useEffect(() => {
    if (defaultOpen && !isOpen) {
      setIsOpen(true);
      setShowRows(true);
      setHeight('auto');
    }
  }, [defaultOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* Header row */}
      <div
        onClick={toggle}
        onPointerDown={() => setPressed(true)}
        onPointerUp={() => setPressed(false)}
        onPointerLeave={() => setPressed(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          cursor: 'pointer',
          backgroundColor: pressed ? '#FAFAFA' : '#fff',
          transition: 'background-color 150ms ease',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div style={{
          flexShrink: 0,
          borderRadius: '50%',
          border: isOpen ? `1.5px solid ${T.gray500}` : '1.5px solid transparent',
          transition: 'border-color 250ms ease',
        }}>{icon}</div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
            {label}
          </span>
          {badge}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 15,
            fontWeight: 700,
            color: totalColor,
            letterSpacing: '-0.01em',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {total}
          </span>
          {/* SVG chevron — right arrow, rotates 0→90° on expand */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            style={{
              flexShrink: 0,
              opacity: 0.4,
              transition: `transform ${DURATION}ms ${EASE}`,
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            <path
              d="M5.5 3L9.5 7L5.5 11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </div>
      </div>

      {/* Expanded content area */}
      <div
        ref={contentRef}
        onTransitionEnd={handleTransitionEnd}
        style={{
          height: typeof height === 'number' ? height : 'auto',
          overflow: 'hidden',
          transition: `height ${DURATION}ms ${EASE}`,
        }}
      >
        <div ref={innerRef}>
          <div style={{
            background: innerBg ?? T.accordionBg,
            boxShadow: 'inset 0 2px 4px -2px rgba(0,0,0,0.08)',
            borderRadius: innerRadius ?? '0 0 12px 12px',
            padding: '4px 0 6px 0',
          }}>
            {children(showRows)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Stagger helper hook ─────────────────────────────────────────
 * Returns true for items up to `index` once `showRows` flips true.
 * 80ms initial delay + 50ms per row.
 */
export function useRowStagger(showRows: boolean, index: number): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!showRows) {
      setVisible(false);
      return;
    }
    const delay = index * 50;
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [showRows, index]);

  return visible;
}

export function StaggerRow({
  showRows,
  index,
  children,
}: {
  showRows: boolean;
  index: number;
  children: ReactNode;
}) {
  const visible = useRowStagger(showRows, index);

  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(-8px)',
      transition: 'opacity 250ms ease, transform 250ms ease',
    }}>
      {children}
    </div>
  );
}

export default AnimatedAccordion;
